import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { DenseTable, StatusBadge, type Column } from "../../components/index.ts";
import type { IdentityMcpSummary } from "./use-identity-mcp.ts";
import { coreStateLevel, coreStateLabel, integrityHref } from "./vocab.ts";

/**
 * Protected_Core status per instance (task 7.3; requirement 13.3 "Protected_Core status per instance
 * linked to `/integrity`").
 *
 * The summary's `core_state` is one row per reporting instance — a `source_id`, an `instance_id` and
 * the latest Protected_Core `core_state` (or `null` when the instance reported no attestation). Each
 * row shows the state as a `StatusBadge` (icon + text, never colour alone; design 12.4): `verified`
 * is healthy, `degraded` warns, `failed`/`absent` are errors, and `other`/unknown is grey, never
 * green (design 12.3). Every row links to `/integrity`, the authoritative Protected_Core attestation
 * and checkpoint view, so an operator who spots a non-verified instance here follows straight through
 * to the detail (requirement 13.3). The whole row is the activation target; the state badge carries
 * the honest summary.
 */

type CoreStateRow = IdentityMcpSummary["core_state"][number];

export function ProtectedCorePanel(props: { coreState: readonly CoreStateRow[] }): ReactNode {
  const navigate = useNavigate();

  // Worst-first: errors (failed/absent), then warnings (degraded), then unknown, then verified, so
  // the instances that need attention sit at the top of the table.
  const sorted = [...props.coreState].sort((a, b) => severity(b.core_state) - severity(a.core_state));

  const columns: readonly Column<CoreStateRow>[] = [
    {
      key: "instance",
      header: "Instance",
      isRowHeader: true,
      cell: (r) => <code className="imcp-core__id">{r.instance_id}</code>,
    },
    {
      key: "source",
      header: "Source",
      cell: (r) => <code className="imcp-core__id">{r.source_id}</code>,
    },
    {
      key: "state",
      header: "Protected_Core",
      cell: (r) => (
        <StatusBadge
          level={coreStateLevel(r.core_state)}
          label={coreStateLabel(r.core_state)}
          size="sm"
          title={`Protected_Core on instance ${r.instance_id} is ${coreStateLabel(r.core_state).toLowerCase()}. Open Integrity for the attestation detail.`}
        />
      ),
    },
    {
      key: "detail",
      header: "",
      align: "right",
      cell: (r) => (
        <button
          type="button"
          className="imcp-link"
          onClick={() => navigate(integrityHref())}
          aria-label={`Open Integrity for the Protected_Core detail of instance ${r.instance_id}`}
        >
          Integrity →
        </button>
      ),
    },
  ];

  return (
    <section className="imcp-core" aria-label="Protected_Core status per instance">
      <div className="imcp-section__head">
        <h2 className="imcp-section__title">Protected_Core status</h2>
        <button
          type="button"
          className="imcp-link"
          onClick={() => navigate(integrityHref())}
          aria-label="Open the Integrity view for full Protected_Core attestation detail"
        >
          Open Integrity →
        </button>
      </div>
      <DenseTable
        caption="Protected_Core state for each reporting Identity MCP instance, linked to Integrity"
        columns={columns}
        rows={sorted}
        rowKey={(r) => `${r.source_id}:${r.instance_id}`}
        onRowClick={() => navigate(integrityHref())}
        rowLabel={(r) => `Open Integrity for instance ${r.instance_id}`}
        stickyHeader
        emptyMessage="No Identity MCP instance reported a Protected_Core state in this window."
      />
    </section>
  );
}

/** Order Protected_Core states worst-first for the table. */
function severity(state: CoreStateRow["core_state"]): number {
  switch (state) {
    case "failed":
    case "absent":
      return 4;
    case "degraded":
      return 3;
    case "other":
    case null:
    case undefined:
      return 2;
    case "verified":
      return 1;
  }
}
